const DB_NAME = 'offline-data';
const DB_VERSION = 1;
const QUOTES = 'quotes';
const SERVICES = 'services';

export interface Quote {
  id: string;
  [key: string]: any;
}

export interface Service {
  id: string;
  [key: string]: any;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    if (typeof indexedDB === 'undefined') {
      return Promise.reject(new Error('IndexedDB not available'));
    }
    dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(QUOTES)) {
          db.createObjectStore(QUOTES, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(SERVICES)) {
          db.createObjectStore(SERVICES, { keyPath: 'id' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }
  return dbPromise;
}

type MemoryDB = {
  quotes: Record<string, Quote>;
  services: Record<string, Service>;
};

const memory: MemoryDB | null = typeof indexedDB === 'undefined'
  ? { quotes: {}, services: {} }
  : null;

async function put(store: string, item: any): Promise<void> {
  if (memory) {
    (memory as any)[store][item.id] = item;
    return;
  }
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.objectStore(store).put(item);
  });
}

async function getAll(store: string): Promise<any[]> {
  if (memory) {
    return Object.values((memory as any)[store]);
  }
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readonly');
    const request = tx.objectStore(store).getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function remove(store: string, id: string): Promise<void> {
  if (memory) {
    delete (memory as any)[store][id];
    return;
  }
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.objectStore(store).delete(id);
  });
}

export function saveQuote(q: Quote): Promise<void> {
  return put(QUOTES, q);
}
export function loadQuotes(): Promise<Quote[]> {
  return getAll(QUOTES) as Promise<Quote[]>;
}
export function deleteQuote(id: string): Promise<void> {
  return remove(QUOTES, id);
}

export function saveService(s: Service): Promise<void> {
  return put(SERVICES, s);
}
export function loadServices(): Promise<Service[]> {
  return getAll(SERVICES) as Promise<Service[]>;
}
export function deleteService(id: string): Promise<void> {
  return remove(SERVICES, id);
}
